import { EventBadges } from "@components/film/EventBadges"
import { PlayTrailerButton } from "@components/media/TrailerOverlay"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { getFilmMeta } from "@features/program/get-film-meta"
import { trackEvent } from "@features/tracking/tracking"
import { WishlistButton } from "@features/wishlist/WishlistButton"
import { useIsMobile } from "@hooks/useIsMobile"
import { useParentSlideTimer, useSlideTimer } from "@hooks/useSliderTimer"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { motion } from "motion/react"
import { ReactNode, useEffect, useState } from "react"

export const meta: BlockMeta = {
  title: "Carousel Item",
  description: "A carousel item",
  category: "media",
  tags: ["#homepage-carousel"],
}

export default defineBlock("homepage/homepage-carousel-item", (props) => {
  const [playingVideo, setPlayingVideo] = useState(false)
  const isMobile = useIsMobile(900)

  let [heading] = useInlineEditableValue<string>("heading")

  const slideTimer = useParentSlideTimer()
  let content: ReactNode = null

  const pauseOnHover = {
    onMouseEnter: () => {
      slideTimer?.pause()
    },
    onMouseLeave: () => {
      if (!playingVideo) {
        slideTimer?.start()
      }
    },
  }

  useEffect(() => {
    if (playingVideo) {
      slideTimer?.pause()
    }
    return () => {
      if (playingVideo) {
        slideTimer?.start()
      }
    }
  }, [playingVideo])

  let videoSrc = ""
  if (props.type === "sizzle") {
    videoSrc = props.videoFile?.mediaItemUrl || ""
    if (isMobile) {
      videoSrc = props.mobileVideoFile?.mediaItemUrl ?? videoSrc
    }
  }

  if (props.type === "sizzle" && videoSrc) {
    heading = "Sizzle Reel"
    content = (
      <>
        <motion.video
          src={videoSrc}
          className="absolute top-0 left-0 w-full h-full object-cover object-center"
          playsInline
          muted={!playingVideo}
          autoPlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onEnded={(e) => {
            setPlayingVideo(false)
            slideTimer?.goNext()
            if (playingVideo) {
              trackEvent("Watched To End Unmuted", {}, e.currentTarget)
            }
          }}
        />
        <div className="absolute z-[1] inset-0 bg-bg/5 text-fg">
          <motion.div
            className="w-grid mx-auto pt-8"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.98 }}
            transition={{ duration: 0.3 }}
          >
            <div className="">
              <Button
                shape="icon"
                variant="ghost"
                href={props.link?.url}
                target={props.link?.target}
                rightIcon={playingVideo ? "unmuted" : "muted"}
                onClick={(e) => {
                  e.preventDefault()
                  setPlayingVideo((prev) => !prev)
                  trackEvent("Unmute Video", {}, e.currentTarget)
                }}
                {...pauseOnHover}
              />
            </div>
          </motion.div>
        </div>
      </>
    )
  } else if (props.type === "custom") {
    content = (
      <>
        <motion.img
          srcSet={props.image?.srcSet!}
          className="absolute z-0 top-0 left-0 w-full h-full object-cover object-center"
          sizes="100vw"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        />
        <div className="absolute z-[1] inset-0 bg-bg/5 text-fg">
          <motion.div
            className="w-grid mx-auto pt-8 [text-shadow:0_0_100px_rgba(0,0,0,0.9)]"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.98 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <EditableText
                as="div"
                store="heading"
                placeholder="Enter a heading"
                className="type-title-xxl w-[10em] mb-4"
              />
            </motion.div>
            <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <EditableText
                as="p"
                store="description"
                placeholder="Enter a description"
                className="type-body-l max-w-[30em] mb-6 text-balance"
              />
            </motion.div>
            <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <div className="">
                {!!props.link?.url && (
                  <Button
                    shape="default"
                    variant="primary"
                    href={props.link?.url}
                    target={props.link?.target}
                    {...pauseOnHover}
                  >
                    {props.link?.title}
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </>
    )
  } else if (props.type === "film" && props.film) {
    const film = props.film!
    const filmMeta = getFilmMeta(film)
    content = (
      <>
        <motion.img
          srcSet={film.media?.thumb?.srcSet!}
          className="absolute z-0 top-0 left-0 w-full h-full object-cover object-center"
          sizes="100vw"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        />
        <div className="absolute z-[1] inset-0 bg-bg/5 text-fg">
          <motion.div
            className="w-grid mx-auto pt-8"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.98 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <EditableText
                as="div"
                store="filmHeading"
                defaultValue={film.title}
                className="type-title-xxl w-[10em] mb-4"
              />
            </motion.div>
            {/* <motion.div className="flex flex-wrap gap-2 mb-4" variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <EventBadges film={film} shape="rounded" />
              {filmMeta.map((meta, i) => (
                <Chip shape="rounded" variant="high" key={i} className="bg-black/30">
                  {meta}
                </Chip>
              ))}
            </motion.div> */}
            <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}>
              <EditableText
                as="p"
                store="description"
                defaultValue={film.excerpt}
                className="type-body-l max-w-[30em] mb-6 text-balance"
              />
            </motion.div>
            <motion.div
              {...pauseOnHover}
              className="flex gap-3 items-center"
              variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}
            >
              {!!film.uri && (
                <Button shape="default" variant="primary" href={film.uri}>
                  <EditableText store="filmLinkTitle" defaultValue="Book Tickets" plainText disableLineBreaks />
                </Button>
              )}

              {props.film?.eventInfo?.trailer && (
                <PlayTrailerButton key="trailer" videoId={props.film?.eventInfo?.trailer} filmUri={props.film.uri!}>
                  <Button shape="default" variant="ghost" leftIcon="play">
                    Play Trailer
                  </Button>
                </PlayTrailerButton>
              )}

              <WishlistButton key="wishlist" film={props.film!} />
            </motion.div>
          </motion.div>
        </div>
      </>
    )
  }

  return (
    <motion.div
      initial="hide"
      animate="show"
      exit="hide"
      transition={{ staggerChildren: 0.06 }}
      className="absolute isolate inset-0 flex items-center justify-center theme-dark overflow-hidden"
      data-mixpanel-slide={heading}
    >
      {content}
    </motion.div>
  )
})
